import React, { useState, useContext, useEffect, useCallback, useMemo } from "react";
import TabGroup from "components/tab-group";
import AdmitTableViewer from "./admit-table-viewer";
import Loader from 'components/partials/loader/loader';
import CustomButton from "components/partials/custom-button/custom-button";
import { FaoResultsContext } from 'context/FaoResultsContext';
import useAdmitAdjustmentHandlers from '../hooks/use-admit-adjustment-handlers';

// assets
import Icons from "../../../assets/icons";

// TABS will be dynamically generated from the API data

/**
 * FaoAdmitAdjust Component
 *
 * This component provides an interactive interface for adjusting admission parameters
 * in the FAO (Financial Aid Optimization) builder. It allows users to:
 * - View and modify admission data across different need ranks and academic ranks
 * - Switch between In-State and Out-of-State student populations
 * - Calculate target admissions based on percentage increases
 * - View historical yield rates for PY
 * - Save adjustments to the model configuration
 *
 * The component handles complex calculations including target admissions,
 * yield rate calculations, and percentage-based adjustments while providing
 * real-time feedback and validation.
 */
const FaoAdmitAdjust = () => {
  // Use the context for admit adjustment data
  const {
    admitAdjustmentData: data,
    admitAdjustmentTabs: tabs,
    admitAdjustmentUserInput,
    loading,
    isSaving,
    isSaved,
  } = useContext(FaoResultsContext);
  
  const { handlePercentageChange, handlePercentageBlur, handleAdmitAdjustmentSave } = useAdmitAdjustmentHandlers();
  const [activeTab, setActiveTab] = useState('');

  /**
   * Dynamically determines save button text based on current state
   * Provides clear feedback to users about the save operation status
   */
  const saveButtonText = useMemo(() => {
    if (isSaving.admitAdjustments) return "Saving...";
    if (isSaved.admitAdjustments) return "Saved";
    return "Save Admit Adjustments";
  }, [isSaving.admitAdjustments, isSaved.admitAdjustments]);

  // Set active tab to first available tab when tabs are loaded
  useEffect(() => {
    if (tabs.length > 0 && !activeTab) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs, activeTab]);

  /**
   * Compose academic data for display, using userInput if present
   */
  const getCalculatedAcademicData = useCallback((regime, needRankMin) => {
    const baseRows = data[regime]?.academicDataByNeedRank?.[needRankMin] || [];
    const userRows = admitAdjustmentUserInput[regime]?.academicDataByNeedRank?.[needRankMin] || [];
    return baseRows.map((row, idx) => {
      const increase = userRows[idx]?.increase ?? row.increase;
      const yieldVal = userRows[idx]?.yield ?? row.yield;
      
      const admits = row.admits;
      const increaseValue = increase === '' ? 0 : (parseFloat(increase) || 0);
      const admitTarget = Math.round(admits * (1 + increaseValue / 100));
      
      const yieldValue = yieldVal === '' ? 0 : (parseFloat(yieldVal) || 0);
      const yieldAdj = 1 + (yieldValue / 100);
      const admitAdj = 1 + (increaseValue / 100);
      
      const calculatedProjYield = admits * admitAdj > 0 
        ? (row.totalProbability * yieldAdj) / (admits * admitAdj)
        : 0;
      const projectedYieldFormatted = `${(calculatedProjYield * 100).toFixed(2)}%`;
      
      return {
        ...row,
        increase,
        yield: yieldVal,
        target: admitTarget,
        projectedYield: projectedYieldFormatted
      };
    });
  }, [data, admitAdjustmentUserInput]);

  /**
   * Calculate summary values for a need rank
   */
  const getSummaryValues = useCallback((regime, needRankMin) => {
    const baseRows = data[regime]?.academicDataByNeedRank?.[needRankMin] || [];
    const userRows = admitAdjustmentUserInput[regime]?.academicDataByNeedRank?.[needRankMin] || [];
    
    if (baseRows.length === 0) {
      return { pyYield: '0.00%', projectedYield: '0.00%' };
    }

    let totalPYAdmits = 0;
    let totalPYEnrolled = 0;
    let totalProjAdmits = 0;
    let totalProjEnrolled = 0;

    baseRows.forEach((row, idx) => {
      const admits = row.admits;
      const pyYield = row.pyYield;
      const enrolled = Math.round(admits * pyYield);
      
      const increase = userRows[idx]?.increase ?? row.increase;
      const yieldVal = userRows[idx]?.yield ?? row.yield;
      
      const increaseValue = increase === '' ? 0 : (parseFloat(increase) || 0);
      const yieldValue = yieldVal === '' ? 0 : (parseFloat(yieldVal) || 0);
      
      const projAdmits = Math.round(admits * (1 + increaseValue / 100));
      const projYield = admits * (1 + increaseValue / 100) > 0 
        ? (row.totalProbability * (1 + yieldValue / 100)) / (admits * (1 + increaseValue / 100))
        : 0;
      const projEnrolled = Math.round(projAdmits * projYield);
      
      totalPYAdmits += admits;
      totalPYEnrolled += enrolled;
      totalProjAdmits += projAdmits;
      totalProjEnrolled += projEnrolled;
    });

    const aggregatedPYYield = totalPYAdmits > 0 ? (totalPYEnrolled / totalPYAdmits) : 0;
    const aggregatedProjYield = totalProjAdmits > 0 ? (totalProjEnrolled / totalProjAdmits) : 0;

    return {
      pyYield: `${(aggregatedPYYield * 100).toFixed(2)}%`,
      projectedYield: `${(aggregatedProjYield * 100).toFixed(2)}%`
    };
  }, [data, admitAdjustmentUserInput]);

  if (loading.admitAdjustments) {
    return <Loader />;
  }

  return (
    <div className="w-full space-y-8">
        {tabs.length > 0 && (
          <TabGroup 
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
        )}

        {/* Tables */}
        {activeTab && data[activeTab] && (
          <div>
            {data[activeTab].needRanks.map((needRank, index) => {
              const calculatedAcademicData = getCalculatedAcademicData(activeTab, needRank.min);
              const summaryValues = getSummaryValues(activeTab, needRank.min);
              return (
                <AdmitTableViewer
                  key={index}
                  needRank={needRank}
                  academicData={calculatedAcademicData}
                  summaryValues={summaryValues}
                  onPercentageChange={(needRankMin, academicRankIndex, field, value) => 
                    handlePercentageChange(activeTab, needRankMin, academicRankIndex, field, value)
                  }
                  onPercentageBlur={(needRankMin, academicRankIndex, field) => 
                    handlePercentageBlur(activeTab, needRankMin, academicRankIndex, field)
                  }
                />
              );
            })}
          </div>
        )}

        {/* Save Button at Bottom */}
        <div className="flex justify-center">
          <CustomButton
            variant="primary"
            onClick={handleAdmitAdjustmentSave}
            disabled={isSaving.admitAdjustments}
            iconBefore={
              // Show spinner icon only during save operation
              isSaving.admitAdjustments && (
                <Icons.Spinner customClass="h-full w-4 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-900" />
              )
            }
          >
            {saveButtonText}
          </CustomButton>
        </div>
    </div>
  );
};

export default FaoAdmitAdjust;
